import { useEffect, useCallback, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ensurePushSubscribed, disablePush, pushSupported } from "@/lib/push";

export type EnableResult =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "denied" | "missing_vapid" | "no_keys" | "no_user" | string };

export function useStreakReminder() {
  const { user, streak } = useAuth();
  const [isEnabled, setIsEnabled] = useState(false);

  const requestPermission = useCallback(async (): Promise<"granted" | "denied" | "default" | "unsupported"> => {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    return await Notification.requestPermission();
  }, []);

  const scheduleReminder = useCallback(() => {
    if (!user) return;
    const lastNotif = localStorage.getItem("mimi-last-notif");
    const today = new Date().toISOString().split("T")[0];
    if (lastNotif === today) return;
    const hour = new Date().getHours();
    if (hour >= 18) {
      const lastActive = localStorage.getItem("mimi-last-active");
      if (lastActive !== today) {
        try {
          new Notification("Inquira Study Reminder 📚", {
            body:
              streak > 0
                ? `Don't lose your ${streak}-day streak! Solve a problem before midnight.`
                : "Time to study! Solve a problem to start a new streak.",
            icon: "/app-icon-192.png",
            tag: "streak-reminder",
          });
          localStorage.setItem("mimi-last-notif", today);
        } catch {
          // Notification constructor can throw on some mobile browsers; ignore.
        }
      }
    }
  }, [user, streak]);

  const enableReminders = useCallback(async (): Promise<EnableResult> => {
    if (!user) return { ok: false, reason: "no_user" };
    const perm = await requestPermission();
    if (perm === "unsupported") return { ok: false, reason: "unsupported" };
    if (perm !== "granted") return { ok: false, reason: "denied" };
    if (pushSupported()) {
      try {
        const res = await ensurePushSubscribed(user.id);
        if (!res.ok) return { ok: false, reason: res.reason ?? "subscribe_failed" };
      } catch (e) {
        console.warn("push subscribe failed", e);
        return { ok: false, reason: (e as Error)?.message ?? "subscribe_failed" };
      }
    }
    localStorage.setItem("mimi-reminders", "on");
    setIsEnabled(true);
    scheduleReminder();
    return { ok: true };
  }, [requestPermission, scheduleReminder, user]);

  const disableReminders = useCallback(() => {
    localStorage.setItem("mimi-reminders", "off");
    setIsEnabled(false);
    if (user) disablePush(user.id).catch(() => {});
  }, [user]);

  useEffect(() => {
    const enabled = localStorage.getItem("mimi-reminders");
    if (enabled !== "on") return;
    scheduleReminder();
    const interval = setInterval(scheduleReminder, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [scheduleReminder]);

  useEffect(() => {
    setIsEnabled(localStorage.getItem("mimi-reminders") === "on");
  }, []);

  return { enableReminders, disableReminders, isEnabled };
}